// Auth-aware Pulse client with deferred connect, channel management, and reconnect/backoff

export type Unsubscribe = () => void;
export type PulseHandler<T = unknown> = (payload: T) => void;

type WSLike = WebSocket;

class PulseServiceImpl {
  // Handlers by topic
  private topics = new Map<string, Set<PulseHandler>>();

  // Connection state
  private ws: WSLike | null = null;
  private token: string | null = null;
  private connecting = false;
  private backoffMs = 1000; // starts at 1s
  private readonly backoffMaxMs = 15000; // cap at 15s
  private reconnectTimer: number | null = null;
  private wsFailCount = 0;
  // WS enablement: can be disabled via env/runtime/localStorage
  private wsEnabledCache: boolean | null = null;

  // Channels
  private desiredChannels: Set<string> = new Set();
  private joinedChannels: Set<string> = new Set();

  // Long-poll fallback
  private polling = false;
  private pollSince: string | number | null = null;
  private pollAbort: AbortController | null = null;
  // Default poll tuning (can be overridden by env/runtime)
  private readonly defaultPollTimeoutSeconds = 10;
  private readonly defaultPollIntervalMsVisible = 1500;
  private readonly defaultPollIntervalMsHidden = 4000;

  // Public API
  subscribe<T = unknown>(topic: string, handler: PulseHandler<T>): Unsubscribe {
    if (!this.topics.has(topic)) this.topics.set(topic, new Set());
    const set = this.topics.get(topic)!;
    set.add(handler as PulseHandler);
    return () => {
      set.delete(handler as PulseHandler);
      if (set.size === 0) this.topics.delete(topic);
    };
  }

  reconnect(): void {
    // Re-open the socket using current token and (potentially) new URL from localStorage
    if (!this.token) return;
    this.disconnect();
    this.openSocket();
  }

  emit<T = unknown>(topic: string, payload: T): void {
    const set = this.topics.get(topic);
    if (!set) return;
    for (const h of set) {
      try { (h as PulseHandler<T>)(payload); } catch (_) { /* ignore */ }
    }
  }

  connect(token: string): void {
    this.token = token || null;
    if (!this.token) return; // defer until we have token
    if (this.ws || this.connecting) return; // already connected/connecting
    if (!this.isWsEnabled()) {
      // Skip WS entirely and start polling
      this.startPolling();
      return;
    }
    this.openSocket();
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.backoffMs = 1000;
    this.joinedChannels.clear();
    if (this.ws) {
      try { this.ws.close(); } catch (_) { /* noop */ }
      this.ws = null;
    }
    this.connecting = false;
    this.wsFailCount = 0;
    this.stopPolling();
  }

  updateToken(token: string | null): void {
    this.token = token || null;
    if (!this.token) {
      this.disconnect();
      return;
    }
    // If connected, re-auth by reopening
    this.disconnect();
    if (!this.isWsEnabled()) {
      this.startPolling();
    } else {
      this.openSocket();
    }
  }

  setChannels(channels: string[]): void {
    const next = new Set(channels.filter(Boolean));
    this.desiredChannels = next;
    // If connected, sync now
    this.syncChannels();
  }

  isConnected(): boolean {
    return this.ws != null && this.ws.readyState === WebSocket.OPEN;
  }

  // Internals
  private openSocket(): void {
    if (!this.token) return;
    if (!this.isWsEnabled()) { this.startPolling(); return; }
    this.connecting = true;
    // Notify UI that we are attempting to connect
    this.emit('pulse:status', { connected: false, connecting: true });
    const url = this.buildWsUrl();
    let ws: WSLike;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.handleWsFailure();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.connecting = false;
      this.backoffMs = 1000; // reset backoff
      this.wsFailCount = 0; // reset failure counter
      this.stopPolling(); // ensure poller is off when WS is up
      // Authenticate (prefer message-level auth)
      this.send({ type: 'auth', token: this.token });
      // Sync channels after auth
      this.syncChannels();
      // Notify UI
      this.emit('pulse:status', { connected: true, connecting: false });
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data));
        // Expected shapes:
        // - { topic: string, data: any }
        // - { type: 'event', topic: string, data: any }
        // - { type: 'system', event: 'joined'|'left'|'error', ... }
        const topic = msg.topic || msg?.data?.topic;
        if (topic) {
          this.emit(topic, msg.data ?? msg.payload ?? msg);
          return;
        }
        // Map known events to app topics if needed
        if (msg.type === 'event' && typeof msg.event === 'string') {
          this.emit(msg.event, msg.payload ?? msg.data ?? {});
          return;
        }
      } catch (_) {
        // ignore malformed
      }
    };

    ws.onerror = () => {
      // Let onclose handle reconnect
    };

    ws.onclose = () => {
      this.ws = null;
      this.connecting = false;
      // Notify UI
      this.emit('pulse:status', { connected: false, connecting: false });
      this.handleWsFailure();
    };
  }

  private handleWsFailure(): void {
    this.wsFailCount += 1;
    if (!this.isWsEnabled()) { this.startPolling(); return; }
    // After 2 consecutive failures, switch to long-poll fallback
    if (this.wsFailCount >= 2) {
      this.startPolling();
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (!this.token) return; // do not reconnect without token
    if (this.reconnectTimer) return;
    const wait = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, this.backoffMaxMs);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.ws && this.token) {
        this.openSocket();
      }
    }, wait) as unknown as number;
  }

  private send(obj: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify(obj)); } catch (_) { /* ignore */ }
  }

  private syncChannels(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // compute diff
    const toJoin: string[] = [];
    const toLeave: string[] = [];
    for (const ch of this.desiredChannels) if (!this.joinedChannels.has(ch)) toJoin.push(ch);
    for (const ch of this.joinedChannels) if (!this.desiredChannels.has(ch)) toLeave.push(ch);
    if (toJoin.length > 0) {
      this.send({ type: 'subscribe', channels: toJoin });
      toJoin.forEach((c) => this.joinedChannels.add(c));
    }
    if (toLeave.length > 0) {
      this.send({ type: 'unsubscribe', channels: toLeave });
      toLeave.forEach((c) => this.joinedChannels.delete(c));
    }
  }

  private buildWsUrl(): string {
    // Allow runtime override via localStorage (useful for staging/testing)
    const lsUrl = (() => {
      try { return localStorage.getItem('pulse.ws.url') || '';} catch { return ''; }
    })();
    const envUrl = (import.meta as any).env?.VITE_PULSE_WS_URL as string | undefined;
    let base = lsUrl || envUrl || '';
    if (!base) {
      // Derive from current origin: http(s) -> ws(s), default path /pulse
      const origin = window.location.origin;
      base = origin.replace(/^http/, 'ws') + '/pulse';
    }
    const url = new URL(base);
    // As a fallback for auth on WS (since headers are limited), include token in query too
    if (this.token) url.searchParams.set('token', this.token);
    return url.toString();
  }

  // ---------- Long-poll fallback ----------
  private startPolling(): void {
    if (this.polling) return;
    // Polling can saturate per-origin connection slots on HTTP/1.1 and delay other API calls.
    // Keep it disabled by default. It can be enabled via localStorage('pulse.poll.enabled') = '1'
    // or env flags when WS is unavailable.
    if (!this.isPollingEnabled()) {
      return; // do not start polling
    }
    this.polling = true;
    this.emit('pulse:status', { connected: false, connecting: true });
    this.loopPoll();
  }

  private stopPolling(): void {
    if (!this.polling) return;
    this.polling = false;
    if (this.pollAbort) {
      try { this.pollAbort.abort(); } catch { /* noop */ }
      this.pollAbort = null;
    }
  }

  // Poll enablement controls (default: disabled)
  private isPollingEnabled(): boolean {
    // LocalStorage override
    try {
      const ls = localStorage.getItem('pulse.poll.enabled');
      if (ls != null) return !(ls === '0' || ls.toLowerCase() === 'false');
    } catch { /* ignore */ }
    // Vite env
    const vite = (import.meta as any)?.env?.VITE_PULSE_POLL_ENABLED as string | undefined;
    if (typeof vite === 'string') return !(vite === '0' || vite.toLowerCase() === 'false');
    // Runtime window.__ENV
    try {
      const runtime = (window as any)?.__ENV?.PULSE_POLL_ENABLED as string | boolean | undefined;
      if (typeof runtime === 'string') return !(runtime === '0' || runtime.toLowerCase() === 'false');
      if (typeof runtime === 'boolean') return runtime;
    } catch { /* ignore */ }
    // Default: disabled to avoid blocking other requests
    return false;
  }

  private async loopPoll(): Promise<void> {
    if (!this.polling || !this.token) return;
    try {
      this.emit('pulse:status', { connected: false, connecting: true });
      const res = await this.doPollRequest();
      if (res === 'ok' || res === 'no-content') {
        // Consider connection alive under long-poll
        this.emit('pulse:status', { connected: true, connecting: false });
      } else {
        this.emit('pulse:status', { connected: false, connecting: false });
      }
    } catch {
      // network or parsing error
      this.emit('pulse:status', { connected: false, connecting: false });
    } finally {
      // Schedule next poll based on visibility
      const delay = document.hidden ? this.getPollIntervalMsHidden() : this.getPollIntervalMsVisible();
      setTimeout(() => this.loopPoll(), delay);
    }
  }

  private async doPollRequest(): Promise<'ok' | 'no-content' | 'error'> {
    const url = new URL('/api/events/notifications', window.location.origin);
    if (this.pollSince != null) url.searchParams.set('since', String(this.pollSince));
    url.searchParams.set('timeout', String(this.getPollTimeoutSeconds()));
    // optional: client-side interval hint for servers that support it
    url.searchParams.set('interval', String(this.getPollIntervalMsVisible()));

    const ac = new AbortController();
    this.pollAbort = ac;
    const headers: Record<string, string> = { 'Accept': 'application/json' };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const resp = await fetch(url.toString(), { method: 'GET', headers, signal: ac.signal });
    if (resp.status === 204) {
      return 'no-content';
    }
    if (resp.ok) {
      // Accept a few shapes:
      // 1) { events: [{topic, data}], since: <cursor> }
      // 2) [{topic, data}, ...]
      // 3) { type: 'event', event: string, payload: any } (single)
      let body: any = null;
      try { body = await resp.json(); } catch { body = null; }
      if (body) {
        if (Array.isArray(body)) {
          for (const ev of body) this.emitFromEventEnvelope(ev);
        } else if (Array.isArray(body.events)) {
          for (const ev of body.events) this.emitFromEventEnvelope(ev);
          if (body.since != null) this.pollSince = body.since;
        } else {
          this.emitFromEventEnvelope(body);
          if (body.since != null) this.pollSince = body.since;
        }
      }
      return 'ok';
    }
    return 'error';
  }

  private emitFromEventEnvelope(ev: unknown): void {
    try {
      const e = ev as any;
      // Expected combos
      const topic: string | undefined = e?.topic || e?.data?.topic || (e?.event as string | undefined);
      const data = e?.data ?? e?.payload ?? e;
      if (topic) this.emit(topic, data);
    } catch { /* noop */ }
  }

  // ---------- WS enablement controls ----------
  private isWsEnabled(): boolean {
    if (this.wsEnabledCache !== null) return this.wsEnabledCache;
    // LocalStorage override: 'pulse.ws.enabled' -> '0' disables, '1' enables
    try {
      const ls = localStorage.getItem('pulse.ws.enabled');
      if (ls != null) {
        this.wsEnabledCache = !(ls === '0' || ls.toLowerCase() === 'false');
        return this.wsEnabledCache;
      }
    } catch { /* ignore */ }
    // Vite env
    const vite = (import.meta as any)?.env?.VITE_PULSE_WS_ENABLED as string | undefined;
    if (typeof vite === 'string') {
      this.wsEnabledCache = !(vite === '0' || vite.toLowerCase() === 'false');
      return this.wsEnabledCache;
    }
    // Runtime window.__ENV
    try {
      const runtime = (window as any)?.__ENV?.PULSE_WS_ENABLED as string | boolean | undefined;
      if (typeof runtime === 'string') {
        this.wsEnabledCache = !(runtime === '0' || runtime.toLowerCase() === 'false');
        return this.wsEnabledCache;
      } else if (typeof runtime === 'boolean') {
        this.wsEnabledCache = runtime;
        return this.wsEnabledCache;
      }
    } catch { /* ignore */ }
    // Default: enabled
    this.wsEnabledCache = true;
    return this.wsEnabledCache;
  }

  public setWsEnabled(enabled: boolean): void {
    try { localStorage.setItem('pulse.ws.enabled', enabled ? '1' : '0'); } catch { /* ignore */ }
    this.wsEnabledCache = enabled;
    // Re-evaluate connection mode
    if (!enabled) {
      // Disable WS: switch to polling
      this.disconnect();
      if (this.token) this.startPolling();
    } else {
      // Enable WS: reconnect if token present
      this.disconnect();
      if (this.token) this.openSocket();
    }
  }

  // ---------- Poll tuning helpers ----------
  private getPollTimeoutSeconds(): number {
    // LocalStorage override
    try {
      const ls = localStorage.getItem('pulse.poll.timeoutSeconds');
      if (ls) return Math.max(3, Number(ls) || this.defaultPollTimeoutSeconds);
    } catch { /* ignore */ }
    // Vite env
    const vite = (import.meta as any)?.env?.VITE_PULSE_POLL_TIMEOUT as string | undefined;
    if (typeof vite === 'string') return Math.max(3, Number(vite) || this.defaultPollTimeoutSeconds);
    // Runtime window.__ENV
    try {
      const runtime = (window as any)?.__ENV?.PULSE_POLL_TIMEOUT as string | number | undefined;
      if (typeof runtime === 'string') return Math.max(3, Number(runtime) || this.defaultPollTimeoutSeconds);
      if (typeof runtime === 'number') return Math.max(3, runtime);
    } catch { /* ignore */ }
    return this.defaultPollTimeoutSeconds;
  }

  private getPollIntervalMsVisible(): number {
    try {
      const ls = localStorage.getItem('pulse.poll.intervalMs');
      if (ls) return Math.max(500, Number(ls) || this.defaultPollIntervalMsVisible);
    } catch { /* ignore */ }
    const vite = (import.meta as any)?.env?.VITE_PULSE_POLL_INTERVAL as string | undefined;
    if (typeof vite === 'string') return Math.max(500, Number(vite) || this.defaultPollIntervalMsVisible);
    try {
      const runtime = (window as any)?.__ENV?.PULSE_POLL_INTERVAL as string | number | undefined;
      if (typeof runtime === 'string') return Math.max(500, Number(runtime) || this.defaultPollIntervalMsVisible);
      if (typeof runtime === 'number') return Math.max(500, runtime);
    } catch { /* ignore */ }
    return this.defaultPollIntervalMsVisible;
  }

  private getPollIntervalMsHidden(): number {
    // re-use the same env var; fall back to hidden default
    try {
      const ls = localStorage.getItem('pulse.poll.intervalHiddenMs');
      if (ls) return Math.max(1000, Number(ls) || this.defaultPollIntervalMsHidden);
    } catch { /* ignore */ }
    const vite = (import.meta as any)?.env?.VITE_PULSE_POLL_INTERVAL_HIDDEN as string | undefined;
    if (typeof vite === 'string') return Math.max(1000, Number(vite) || this.defaultPollIntervalMsHidden);
    try {
      const runtime = (window as any)?.__ENV?.PULSE_POLL_INTERVAL_HIDDEN as string | number | undefined;
      if (typeof runtime === 'string') return Math.max(1000, Number(runtime) || this.defaultPollIntervalMsHidden);
      if (typeof runtime === 'number') return Math.max(1000, runtime);
    } catch { /* ignore */ }
    return this.defaultPollIntervalMsHidden;
  }
}

const PulseService = new PulseServiceImpl();
export default PulseService;
